<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useGtag } from '@/composition/use-gtag';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import {
    markUpgradeSeen,
    setSnooze,
  } from '@/composition/printing/use-printing-upgrade';

  /**
   * Upgrade interstitial shown when a legacy-plugin session opens the print
   * settings. The follow-up actions come as function props from the
   * dispatcher (use-printing-settings-modal.ts), which owns modal routing.
   */
  const props = defineProps<{
    onAccept: () => void;
    onLater: () => void;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const gtag = useGtag();
  const restaurantStore = useRestaurantStore();

  const restaurantId = computed(() => restaurantStore.restaurant?.id ?? null);

  const BENEFIT_KEYS = [
    'printingV2.upgradeBenefit1',
    'printingV2.upgradeBenefit2',
    'printingV2.upgradeBenefit3',
    'printingV2.upgradeBenefit4',
  ];

  function track(action: string) {
    gtag.event(action, {
      event_category: 'print_upgrade',
      event_label: restaurantId.value,
    });
  }

  onMounted(() => {
    markUpgradeSeen();
    track('shown');
  });

  function accept() {
    track('accept');
    emit('close');
    props.onAccept();
  }

  function later() {
    track('snooze');
    setSnooze(Date.now());
    emit('close');
    props.onLater();
  }

  function dismiss() {
    track('snooze');
    setSnooze(Date.now());
    emit('close');
  }
</script>

<template>
  <div class="printing-upgrade-modal">
    <div class="upgrade-header">
      <div class="upgrade-header-main">
        <div class="upgrade-icon-tile">
          <PosUiIcon name="printer" :size="26" />
        </div>
        <h2 class="upgrade-title">{{ $t('printingV2.upgradeTitle') }}</h2>
      </div>
      <button
        class="upgrade-close"
        type="button"
        :title="$t('printingV2.close')"
        @click="dismiss"
      >
        <PosUiIcon name="close" :size="22" />
      </button>
    </div>

    <div class="upgrade-body">
      <p class="upgrade-intro">{{ $t('printingV2.upgradeIntro') }}</p>

      <ul class="upgrade-benefits">
        <li v-for="key in BENEFIT_KEYS" :key="key" class="upgrade-benefit">
          <PosUiIcon name="check-circle" class="upgrade-benefit-icon" />
          <span class="upgrade-benefit-text">{{ $t(key) }}</span>
        </li>
      </ul>
    </div>

    <div class="upgrade-footer">
      <PosUiButton color="neutral" variant="outline" size="lg" @click="later">
        {{ $t('printingV2.upgradeLater') }}
      </PosUiButton>
      <PosUiButton color="primary" size="lg" @click="accept">
        {{ $t('printingV2.upgradeCta') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .printing-upgrade-modal {
    @apply tw-flex tw-flex-col tw-bg-white;

    .upgrade-header {
      @apply tw-flex tw-items-start tw-justify-between tw-gap-3 tw-px-6 tw-pt-6;

      .upgrade-header-main {
        @apply tw-flex tw-items-center tw-gap-3;

        .upgrade-icon-tile {
          @apply tw-flex tw-h-12 tw-w-12 tw-flex-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-orange-50 tw-text-orange-600;
        }

        .upgrade-title {
          @apply tw-m-0 tw-text-lg tw-font-bold tw-text-neutral-900;
        }
      }

      .upgrade-close {
        @apply tw-flex tw-h-9 tw-w-9 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 tw-transition-colors hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .upgrade-body {
      @apply tw-px-6 tw-py-4;

      .upgrade-intro {
        @apply tw-m-0 tw-text-sm tw-leading-relaxed tw-text-neutral-600;
      }

      .upgrade-benefits {
        @apply tw-m-0 tw-mt-4 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-p-0;

        .upgrade-benefit {
          @apply tw-flex tw-items-center tw-gap-2.5;

          .upgrade-benefit-icon {
            @apply tw-flex-none tw-text-xl tw-text-emerald-500;
          }

          .upgrade-benefit-text {
            @apply tw-text-sm tw-text-neutral-700;
          }
        }
      }
    }

    .upgrade-footer {
      @apply tw-flex tw-justify-end tw-gap-3 tw-border-t tw-border-neutral-100 tw-px-6 tw-py-4;
    }
  }
</style>
